/**
 * The three checks at the door, as plain maths over what the meeting's own
 * media measured: the camera's real track size, the microphone's level, and
 * the light in the picture. Each says ok, a note worth knowing, or a plain
 * fix; "wait" only until the first reading. Nothing here stops the host going
 * live: the checks tell them what the room will see.
 */

type CheckStatus = "ok" | "note" | "fix" | "wait";

export interface ReadyCheck {
  status: CheckStatus;
  line: string;
}

const ok = (line: string): ReadyCheck => ({ status: "ok", line });
/** Worth knowing, never worth stopping for. */
const note = (line: string): ReadyCheck => ({ status: "note", line });
const fix = (line: string): ReadyCheck => ({ status: "fix", line });
const wait = (line: string): ReadyCheck => ({ status: "wait", line });

// --- camera ---
export interface CameraReading {
  on: boolean;
  /** The track's real size, from `getSettings()`; zero until it runs. */
  width: number;
  height: number;
  /** Why there is no camera, in plain words. */
  problem: string | null;
}

export function cameraCheck(cam: CameraReading): ReadyCheck {
  if (cam.problem) return fix(cam.problem);
  if (!cam.on) return fix("Your camera is off");
  if (!cam.width || !cam.height) return wait("Starting your camera");
  const size = `${cam.width} x ${cam.height}`;
  // A laptop camera is often 640 x 480. It is softer, not broken, so it says so and lets them go.
  return cam.width >= 1280 && cam.height >= 720 ? ok(`HD, ${size}`) : note(`${size}. It will look softer to others.`);
}

// --- microphone ---
/** A level the microphone clearly heard someone at: speech runs about 0.05 to 0.3. */
const HEARD_LEVEL = 0.02;

/**
 * A level that is digital silence, not a quiet room: about -70 dB (0.0003). A room with nobody talking still reads
 * 0.001 or more, so this never fires for a microphone that merely hears no speech.
 */
export const SILENT_LEVEL = 0.0003;
/** How long a microphone must stay silent before the door says so: Chrome's remembered pick can be a dead device. */
export const SILENT_AFTER_MS = 6000;

/** The level of a stretch of sound: the root mean square of its samples (-1 to 1). */
export function levelOf(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += s * s;
  return Math.sqrt(sum / samples.length);
}

/** A device's name without the browser's "Default - " and the USB id on the end. */
export function deviceName(label: string): string {
  return label
    .replace(/^(Default|Communications) - /, "")
    .replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)$/i, "")
    .trim();
}

export interface MicReading {
  on: boolean;
  /** The loudest level of the last few seconds. */
  loudest: number;
  label: string;
  problem: string | null;
  /** The page may not play or hear sound until there has been a click in it. */
  needsClick: boolean;
  /** How long the level has stayed at silence level, in milliseconds; zero as soon as it is not. */
  silentMs: number;
}

/** True when the chosen microphone has been dead silent long enough and there is another one to try. */
export function micSilent(mic: MicReading, others: number): boolean {
  return mic.on && !mic.problem && !mic.needsClick && others > 0 && mic.silentMs >= SILENT_AFTER_MS;
}

/** The other microphones, the system default first (Chrome names it "Default - ..."). */
export function otherMics<T extends { id: string; label: string }>(mics: T[], chosen: string): T[] {
  const rest = mics.filter((m) => m.id !== chosen);
  const isDefault = (m: T) => m.id === "default" || m.label.startsWith("Default - ");
  return [...rest.filter(isDefault), ...rest.filter((m) => !isDefault(m))];
}

export function micCheck(mic: MicReading, others = 0): ReadyCheck {
  if (mic.problem) return fix(mic.problem);
  if (!mic.on) return fix("Your microphone is off");
  if (mic.needsClick) return fix("Click anywhere to test it");
  if (micSilent(mic, others)) return fix("This microphone is silent");
  if (mic.loudest < HEARD_LEVEL) return fix("Say something so we can hear you");
  const name = deviceName(mic.label);
  return ok(name ? `${name}, good level` : "Good level");
}

// --- light ---
/** Darker than this, on average, and faces go grey and grainy. */
export const DARK_BELOW = 60;

/** The average brightness (0 to 255) of RGBA pixels, weighted the way eyes see. */
export function brightnessOf(rgba: Uint8ClampedArray): number {
  const pixels = Math.floor(rgba.length / 4);
  if (pixels === 0) return 0;
  let sum = 0;
  for (let i = 0; i < pixels * 4; i += 4) sum += rgba[i] * 0.299 + rgba[i + 1] * 0.587 + rgba[i + 2] * 0.114;
  return sum / pixels;
}

export function lightCheck(brightness: number | null): ReadyCheck {
  if (brightness === null) return wait("Checking the light");
  return brightness < DARK_BELOW ? fix("Too dark. Face a lamp or a window.") : ok("Your face is bright enough");
}

// --- when there is no camera or microphone ---
/** Why the door has no camera or microphone, in one plain sentence. */
export function refusedWords(camera: CameraReading, mic: MicReading): string {
  if (camera.problem && mic.problem) return "Your camera and microphone did not start. You can still come in.";
  const why = camera.problem ?? mic.problem;
  return why ? `${why}. You can still come in.` : "Your camera did not start. You can still come in.";
}
